import AudioWaveform, {useAudioPeaks} from './AudioWaveform';
import React, {useRef, useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {formatTime, sourceToEdited, editedToSource, clamp} from '../lib/editModel';
import {sequenceClips, sequenceDuration, trimClip, moveClip} from '../lib/sequence';

export default function Timeline({project, videoUrl, currentTime, activeClipId, onSeek, onChange, onInteractionStart}) {
  const audio = useAudioPeaks(videoUrl);
  const [zoom, setZoom] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const trim = useRef(null);
  const dragged = useRef(null);
  const tracks = useRef(null);
  const shown = draft ?? project;
  const intervals = sequenceClips(shown);
  const total = sequenceDuration(shown);
  const scale = Math.max(total, 0.001);
  const selectedIndex = intervals.findIndex(part => part.id === selectedId);
  const selected = intervals[selectedIndex];
  const step = ['confirmed-cfr', 'detected-cfr', 'demo-cfr'].includes(project.frameStatus) ? project.fpsDen / project.fpsNum : 0.01;
  const ticks = Array.from({length: 7}, (_, index) => index * total / 6);
  let position = 0;
  const clips = intervals.map(part => {
    const clip = {...part, position};
    position += part.end - part.start;
    return clip;
  });
  const apply = next => {
    if (next !== project && JSON.stringify(sequenceClips(next)) !== JSON.stringify(sequenceClips(project))) onChange(next);
  };
  const seekAt = event => {
    if (!total || trim.current || !tracks.current) return;
    const rect = tracks.current.getBoundingClientRect();
    if (rect.width) onSeek(editedToSource(clamp((event.clientX - rect.left) / rect.width, 0, 1) * total, intervals));
  };
  const startTrim = (event, part, edge) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    onInteractionStart?.();
    setSelectedId(part.id);
    trim.current = {project, id: part.id, edge, boundary: part[edge], x: event.clientX,
      secondsPerPixel: total / tracks.current.getBoundingClientRect().width, next: project};
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const moveTrim = event => {
    const state = trim.current;
    if (!state) return;
    state.next = trimClip(state.project, state.id, state.edge, state.boundary + (event.clientX - state.x) * state.secondsPerPixel);
    setDraft(state.next);
  };
  const finishTrim = (event, cancel = false) => {
    if (!trim.current) return;
    event.stopPropagation();
    const next = trim.current.next;
    trim.current = null;
    setDraft(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    if (!cancel) apply(next);
  };

  return <section className="timeline panel">
    <div className="timeline-header">
      <div><span className="eyebrow">SEQUENCE / 03</span><h2>Timeline <span className="timeline-caption">Drag edges to trim · drag clips to reorder</span></h2></div>
      <div className="timeline-zoom"><button aria-label="Zoom timeline out" disabled={zoom === 1} onClick={() => setZoom(Math.max(1, zoom / 2))}>−</button><span>{zoom}×</span><button aria-label="Zoom timeline in" disabled={zoom === 16} onClick={() => setZoom(Math.min(16, zoom * 2))}>+</button></div><div className="duration-stats"><span>ORIGINAL <strong>{formatTime(project.duration)}</strong></span><span>EDITED <strong className="accent-text">{formatTime(total)}</strong></span></div>
    </div>
    <div className="timeline-content">
      <div className="timeline-gutter"><SafeIcon icon={FiIcons.FiFilm} /><span>V1</span><SafeIcon icon={FiIcons.FiVolume2} /><span>A1</span></div>
      <div className="timeline-scroll"><div className="track-area" style={{width: `${zoom * 100}%`}}>
        <div className="ruler">{ticks.map((tick, index) => <span key={index} style={{left: `${index / 6 * 100}%`}}>{formatTime(tick)}</span>)}</div>
        <div className="tracks" ref={tracks} onClick={seekAt}>
          {clips.map((part, index) => <React.Fragment key={part.id}>
            <div className={`track-clip video-clip ${selectedId === part.id ? 'clip-selected' : ''} ${dropTarget?.id === part.id ? `drop-${dropTarget.after ? 'after' : 'before'}` : ''}`}
              style={{left: `${part.position / scale * 100}%`, width: `${(part.end - part.start) / scale * 100}%`}}
              draggable={!trim.current} title={`Clip ${index + 1} · source ${part.start.toFixed(2)}–${part.end.toFixed(2)}s`}
              onClick={event => {event.stopPropagation(); setSelectedId(part.id); seekAt(event);}}
              onDragStart={event => {
                if (event.target.closest('.trim-handle') || trim.current) {event.preventDefault(); return;}
                onInteractionStart?.();
                dragged.current = part.id;
                setSelectedId(part.id);
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', part.id);
              }}
              onDragOver={event => {
                if (!dragged.current) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = 'move';
                const rect = event.currentTarget.getBoundingClientRect();
                setDropTarget({id: part.id, after: event.clientX > rect.left + rect.width / 2});
              }}
              onDrop={event => {
                event.preventDefault(); event.stopPropagation();
                const from = intervals.findIndex(clip => clip.id === dragged.current);
                const rect = event.currentTarget.getBoundingClientRect();
                let to = index + (event.clientX > rect.left + rect.width / 2 ? 1 : 0);
                if (from < to) to--;
                apply(moveClip(project, from, to));
                dragged.current = null; setDropTarget(null);
              }} onDragEnd={() => {dragged.current = null; setDropTarget(null);}}>
              <SafeIcon icon={FiIcons.FiFilm} /><span className="clip-name">Clip {String(index + 1).padStart(2, '0')}</span>
              {['start', 'end'].map(edge => <button key={edge} className={`trim-handle trim-${edge}`} aria-label={`Trim ${edge} of clip ${index + 1}`} title={`Drag to trim ${edge}; arrow keys fine-tune`}
                onClick={event => event.stopPropagation()}
                onPointerDown={event => startTrim(event, part, edge)} onPointerMove={moveTrim}
                onPointerUp={finishTrim} onPointerCancel={event => finishTrim(event, true)}
                onKeyDown={event => {
                  if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
                  event.preventDefault(); event.stopPropagation();
                  setSelectedId(part.id);
                  apply(trimClip(project, part.id, edge, part[edge] + (event.key === 'ArrowLeft' ? -step : step)));
                }} />)}
            </div>
            <div className={`track-clip audio-clip ${selectedId === part.id ? 'clip-selected' : ''}`} style={{left: `${part.position / scale * 100}%`, width: `${(part.end - part.start) / scale * 100}%`}}>
              <AudioWaveform audio={audio} start={part.start} end={part.end} />
            </div>
          </React.Fragment>)}
          {total > 0 && audio.status !== 'ready' && <span className="waveform-status" role="status">{audio.status === 'loading' ? 'Generating audio waveform…' : audio.status === 'unavailable' ? 'Waveform unavailable for this audio format' : 'Load media to view its waveform'}</span>}
          {!total && <span className="timeline-empty">No footage in this edit</span>}
          <div className="playhead" style={{left: `${sourceToEdited(currentTime, intervals, activeClipId) / scale * 100}%`}}><span /></div>
        </div>
      </div></div>
    </div>
    {!!clips.length && <div className="clip-inspector">
      <select aria-label="Select timeline clip" value={selected ? selectedId : ''} onChange={event => setSelectedId(event.target.value)}>
        <option value="" disabled>Select a clip to fine-tune</option>
        {clips.map((part, index) => <option key={part.id} value={part.id}>Clip {index + 1}</option>)}
      </select>
      {selected && <>
        {['start', 'end'].map(edge => <label key={`${selected.id}-${edge}-${selected[edge]}`}>Source {edge === 'start' ? 'in' : 'out'} (s)
          <input aria-label={`Clip source ${edge}`} type="number" step={step} min="0" max={project.duration} defaultValue={selected[edge].toFixed(3)}
            onKeyDown={event => {if (event.key === 'Enter') event.currentTarget.blur();}}
            onBlur={event => {if (event.target.value.trim()) apply(trimClip(project, selected.id, edge, Number(event.target.value)));}} />
        </label>)}
        <button disabled={selectedIndex === 0} onClick={() => apply(moveClip(project, selectedIndex, selectedIndex - 1))}>← Move earlier</button>
        <button disabled={selectedIndex === clips.length - 1} onClick={() => apply(moveClip(project, selectedIndex, selectedIndex + 1))}>Move later →</button>
        <span>Video + audio linked · Undo available</span>
      </>}
    </div>}
    <div className="timeline-foot"><span><i className="legend-block" /> Retained footage</span><span>Gaps close automatically</span><span className="foot-right">Edges extend into available source footage</span></div>
  </section>;
}
